import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import BadgeIcon from '@mui/icons-material/Badge';
import CakeIcon from '@mui/icons-material/Cake';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { ClienteItem, useClientesApi } from '../../../services/api/clientesApi';
import PageLayout from '../../../components/layout/PageLayout';
import { DarkModeContext } from '../../../context/darkModeContext';

interface VentaData { fecha: string; monto: number; }
interface UltimasVentas { id: number; fecha: string; monto: number; estado: string; }

const ACCENT = '#7451f8';

const safeFormat = (date: string) => {
  try { return format(new Date(date), 'dd/MM/yyyy'); } catch { return date || '—'; }
};

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string | undefined; dark: boolean }> = ({ icon, label, value, dark }) => (
  <Box display="flex" alignItems="flex-start" gap={1.5} py={0.8}>
    <Box sx={{ color: ACCENT, mt: '2px', flexShrink: 0 }}>{icon}</Box>
    <Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: dark ? '#9b9a97' : '#6b6b6b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.9rem', color: dark ? '#e0e0e0' : '#333', mt: '1px' }}>
        {value || '—'}
      </Typography>
    </Box>
  </Box>
);

const SectionTitle: React.FC<{ label: string; dark: boolean }> = ({ label, dark }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, pl: 1.5, py: 0.5, borderLeft: `3px solid ${ACCENT}`, bgcolor: dark ? 'rgba(116,81,248,0.08)' : 'rgba(116,81,248,0.05)', borderRadius: '0 6px 6px 0' }}>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: ACCENT, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
      {label}
    </Typography>
  </Box>
);

const estadoColor = (estado: string) => {
  const e = estado?.toLowerCase();
  if (e === 'finalizada') return { color: '#2e7d32', bg: 'rgba(46,125,50,0.1)' };
  if (e === 'anulada') return { color: '#c62828', bg: 'rgba(198,40,40,0.1)' };
  if (e === 'pendiente') return { color: '#e65100', bg: 'rgba(230,81,0,0.1)' };
  return { color: '#1565c0', bg: 'rgba(21,101,192,0.1)' };
};

const ClienteDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { darkMode } = useContext(DarkModeContext);
  const [cliente, setCliente] = useState<ClienteItem | null>(null);
  const [ventasData, setVentasData] = useState<VentaData[]>([]);
  const [ultimasVentas, setUltimasVentas] = useState<UltimasVentas[]>([]);
  const [loading, setLoading] = useState(true);
  const { getById, getVentasCliente } = useClientesApi();

  useEffect(() => {
    const load = async () => {
      try {
        if (id) {
          const [clienteRes, ventasRes] = await Promise.all([
            getById(Number(id)),
            getVentasCliente(Number(id)),
          ]);
          if (clienteRes?.cliente) setCliente(clienteRes.cliente);
          if (ventasRes) {
            setVentasData(ventasRes.ventasUltimos3Meses);
            setUltimasVentas(ventasRes.ultimas10Ventas);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const getImageUrl = (path: string) => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:4020';
    return `${base}${path}`;
  };

  const getInitials = (name: string) =>
    name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

  const card = {
    border: `1px solid ${darkMode ? '#333' : '#e9e9e7'}`,
    borderRadius: '12px',
    bgcolor: darkMode ? '#202020' : '#fff',
    p: 2.5,
  };

  return (
    <PageLayout icon={PersonIcon} title="Detalle de Cliente">
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Botón volver */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/administracion/clientes')}
          sx={{ mb: 2.5, borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: ACCENT, bgcolor: 'rgba(116,81,248,0.08)', '&:hover': { bgcolor: 'rgba(116,81,248,0.15)' } }}
        >
          Volver a Clientes
        </Button>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: ACCENT }} /></Box>
        ) : !cliente ? (
          <Typography color="error">No se encontró el cliente.</Typography>
        ) : (
          <Grid container spacing={2.5}>
            {/* ─── Tarjeta de perfil ─── */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={card}>
                <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
                  {/* Avatar */}
                  {cliente.Imagen ? (
                    <Box component="img"
                      src={getImageUrl(cliente.Imagen)}
                      alt={cliente.NombreCompleto}
                      sx={{ width: 90, height: 90, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${ACCENT}`, flexShrink: 0 }}
                    />
                  ) : (
                    <Avatar sx={{ width: 90, height: 90, bgcolor: ACCENT, fontSize: '2rem', border: `3px solid ${ACCENT}`, flexShrink: 0 }}>
                      {getInitials(cliente.NombreCompleto)}
                    </Avatar>
                  )}

                  {/* Nombre y chips */}
                  <Box flex={1}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: darkMode ? '#fff' : '#111' }}>
                      {cliente.NombreCompleto}
                    </Typography>
                    <Box display="flex" gap={1} mt={0.8} flexWrap="wrap">
                      <Chip
                        label={cliente.Activo ? 'Activo' : 'Inactivo'}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: cliente.Activo ? 'rgba(46,125,50,0.12)' : 'rgba(198,40,40,0.12)', color: cliente.Activo ? '#2e7d32' : '#c62828', border: 'none' }}
                      />
                      {cliente.tipoCliente?.Nombre && (
                        <Chip label={cliente.tipoCliente.Nombre} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: 'rgba(116,81,248,0.1)', color: ACCENT }} />
                      )}
                      {cliente.tipoDocumento?.Nombre && (
                        <Chip label={cliente.tipoDocumento.Nombre} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                      )}
                    </Box>
                  </Box>

                  {/* Doc */}
                  <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    <Typography sx={{ fontSize: '0.72rem', color: darkMode ? '#9b9a97' : '#6b6b6b', textTransform: 'uppercase', fontWeight: 700 }}>Documento</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: darkMode ? '#e0e0e0' : '#333' }}>{cliente.NumeroDocumento}</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* ─── Columna izquierda: Datos ─── */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ ...card, height: '100%' }}>
                <SectionTitle label="Información Personal" dark={darkMode} />
                <InfoRow icon={<BadgeIcon fontSize="small" />} label="Número de documento" value={cliente.NumeroDocumento} dark={darkMode} />
                <Divider sx={{ my: 0.5, opacity: 0.4 }} />
                <InfoRow icon={<CakeIcon fontSize="small" />} label="Fecha de nacimiento" value={safeFormat(cliente.FechaNacimiento)} dark={darkMode} />

                <Box sx={{ mt: 2 }}>
                  <SectionTitle label="Contacto" dark={darkMode} />
                  <InfoRow icon={<EmailIcon fontSize="small" />} label="Correo electrónico" value={cliente.Correo} dark={darkMode} />
                  <Divider sx={{ my: 0.5, opacity: 0.4 }} />
                  <InfoRow icon={<PhoneIcon fontSize="small" />} label="Teléfono" value={cliente.Telefono} dark={darkMode} />
                  <Divider sx={{ my: 0.5, opacity: 0.4 }} />
                  <InfoRow icon={<HomeIcon fontSize="small" />} label="Dirección" value={cliente.Direccion} dark={darkMode} />
                </Box>
              </Paper>
            </Grid>

            {/* ─── Columna derecha: Ventas ─── */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={2.5}>
                {/* Gráfica */}
                <Grid item xs={12}>
                  <Paper elevation={0} sx={card}>
                    <SectionTitle label="Ventas Últimos 3 Meses" dark={darkMode} />
                    {ventasData.length === 0 ? (
                      <Typography sx={{ color: darkMode ? '#9b9a97' : '#6b6b6b', fontSize: '0.88rem', textAlign: 'center', py: 4 }}>
                        Sin registros de ventas
                      </Typography>
                    ) : (
                      <Box sx={{ width: '100%', height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={ventasData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#333' : '#eee'} />
                            <XAxis dataKey="fecha" tick={{ fontSize: 11, fill: darkMode ? '#9b9a97' : '#666' }} />
                            <YAxis tick={{ fontSize: 11, fill: darkMode ? '#9b9a97' : '#666' }} />
                            <Tooltip
                              contentStyle={{ backgroundColor: darkMode ? '#2b2b2b' : '#fff', border: `1px solid ${darkMode ? '#333' : '#eee'}`, borderRadius: 8, fontSize: 12 }}
                              formatter={(v) => [`${v} ventas`, 'Cantidad']}
                            />
                            <Bar dataKey="monto" fill={ACCENT} name="Ventas" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* Tabla últimas ventas */}
                <Grid item xs={12}>
                  <Paper elevation={0} sx={card}>
                    <SectionTitle label="Últimas Transacciones" dark={darkMode} />
                    {ultimasVentas.length === 0 ? (
                      <Typography sx={{ color: darkMode ? '#9b9a97' : '#6b6b6b', fontSize: '0.88rem', textAlign: 'center', py: 3 }}>
                        Sin transacciones registradas
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['Fecha', 'Monto', 'Estado'].map(h => (
                                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: darkMode ? '#9b9a97' : '#6b6b6b', borderBottom: `1px solid ${darkMode ? '#333' : '#e9e9e7'}` }}>
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ultimasVentas.map(v => {
                              const { color, bg } = estadoColor(v.estado);
                              return (
                                <TableRow key={v.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                                  <TableCell sx={{ fontSize: '0.85rem' }}>{safeFormat(v.fecha)}</TableCell>
                                  <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600 }}>$ {v.monto.toFixed(2)}</TableCell>
                                  <TableCell>
                                    <Box component="span" sx={{ px: 1.2, py: 0.3, borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700, color, bgcolor: bg }}>
                                      {v.estado}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
    </PageLayout>
  );
};

export default ClienteDetailView;
